import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, User, Package, DollarSign, FileText } from 'lucide-react';

interface Complaint {
  complaint_id: number;
  customer_contact: string;
  complaint_date: string;
  defect_description: string;
  severity: string;
  status: string;
  cost_impact_estimate: number;
  business_impact?: string;
  customer: { name?: string };
  product: { name?: string };
  batch: { batch_number?: string };
}

interface ComplaintViewModalProps {
  complaint: Complaint;
  open: boolean;
  onClose: () => void;
}

export const ComplaintViewModal: React.FC<ComplaintViewModalProps> = ({
  complaint,
  open,
  onClose,
}) => {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Low': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Register': return 'bg-blue-100 text-blue-800';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800';
      case 'Resolved': return 'bg-green-100 text-green-800';
      case 'Closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Complaint Details #{complaint.complaint_id}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status and Severity */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Status:</span>
              <Badge className={getStatusColor(complaint.status)}>
                {complaint.status}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Severity:</span>
              <Badge className={getSeverityColor(complaint.severity)}>
                {complaint.severity}
              </Badge>
            </div>
          </div>

          {/* Customer Information */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Customer Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-600">Name:</span>
                  <p className="text-gray-900">{complaint.customer?.name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Contact:</span>
                  <p className="text-gray-900">{complaint.customer_contact}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Product Information */}
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Package className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold text-gray-900">Product Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-600">Product:</span>
                  <p className="text-gray-900">{complaint.product?.name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Batch:</span>
                  <p className="text-gray-900">{complaint.batch?.batch_number || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Complaint Details */}
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5 text-orange-600" />
                <h3 className="font-semibold text-gray-900">Complaint Details</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-600">Defect Description:</span>
                  <p className="text-gray-900 mt-1 p-3 bg-white rounded-lg border">
                    {complaint.defect_description}
                  </p>
                </div>
                {complaint.business_impact && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Business Impact:</span>
                    <p className="text-gray-900">{complaint.business_impact}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Financial Impact */}
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="h-5 w-5 text-red-600" />
                <h3 className="font-semibold text-gray-900">Financial Impact</h3>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Cost Impact Estimate:</span>
                <p className="text-2xl font-bold text-red-600">
                  ₹{complaint.cost_impact_estimate.toLocaleString()}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-5 w-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900">Timeline</h3>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Complaint Date:</span>
                <p className="text-gray-900">
                  {new Date(complaint.complaint_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};